import { ISelfResponse, IUpdateSelfRequest } from '@/shared/interfaces';
import {
    Input,
    Modal,
    Button,
    FormErrorText,
    VerticalContainer,
} from '@/components';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { R as GlobalResources } from '@/Resources.resx';
import { useForm } from 'react-hook-form';
import {
    isEmail,
    isFullName,
    isPhoneNumber,
    isNationalIdentityNumber,
} from '@/shared/validators';
import { updateSelf } from '../api/updateSelf';
import { useMutation } from '@tanstack/react-query';
import { Utils } from '@/shared/Utils';

type MyInfoModalProps = {
    selfOnOpen: IUpdateSelfRequest;
    onClose: (selfResponse?: ISelfResponse) => void;
};

export const MyInfoModal = ({ selfOnOpen, onClose }: MyInfoModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IUpdateSelfRequest>({ mode: 'onBlur', values: selfOnOpen });

    const updateSelfMutation = useMutation({
        mutationFn: updateSelf,
    });

    const onSubmit = async (data: IUpdateSelfRequest) => {
        if (updateSelfMutation.isPending) return;
        updateSelfMutation.mutate(data, {
            onSuccess: (data) => onClose(data),
        });
    };

    const onCancel = () => onClose();

    return (
        <Modal onClose={onCancel} title={R.Edit}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <main className="py-6">
                    <VerticalContainer spacing="md">
                        {!selfOnOpen.Name && (
                            <FormControl
                                label={R.Name}
                                error={
                                    errors.Name && GlobalResources.NameRequired
                                }
                            >
                                <Input
                                    {...register('Name', {
                                        validate: isFullName,
                                    })}
                                    maxLength={250}
                                    invalid={!!errors.Name}
                                />
                            </FormControl>
                        )}
                        {!selfOnOpen.CPR && (
                            <FormControl
                                label={R.CprNumber}
                                error={errors.CPR && GlobalResources.InvalidCpr}
                            >
                                <Input
                                    placeholder={GlobalResources.CprPlaceholder}
                                    {...register('CPR', {
                                        setValueAs: (value) =>
                                            value.replace('-', ''),
                                        validate: (value: string) =>
                                            isNationalIdentityNumber(value),
                                    })}
                                    maxLength={11}
                                    invalid={!!errors.CPR}
                                    onInput={
                                        Utils.sanitizeNationalIdentifierInput
                                    }
                                />
                            </FormControl>
                        )}

                        <FormControl
                            label={R.Email}
                            error={errors.Email && GlobalResources.InvalidEmail}
                        >
                            <Input
                                {...register('Email', {
                                    validate: isEmail,
                                })}
                                maxLength={256}
                                invalid={!!errors.Email}
                            />
                        </FormControl>

                        <FormControl
                            label={R.Phone}
                            error={
                                errors.Mobile &&
                                GlobalResources.PhoneNumberRequired
                            }
                        >
                            <Input
                                type="tel"
                                {...register('Mobile', {
                                    validate: isPhoneNumber,
                                })}
                                maxLength={20}
                                invalid={!!errors.Mobile}
                            />
                        </FormControl>

                        {updateSelfMutation.isError && (
                            <FormErrorText
                                message={updateSelfMutation.error.message}
                            />
                        )}
                    </VerticalContainer>
                </main>

                <footer className="text-right">
                    <Button
                        variant="primary"
                        type="submit"
                        className={
                            updateSelfMutation.isPending
                                ? 'button--loading'
                                : ''
                        }
                    >
                        {R.Save}
                    </Button>
                </footer>
            </form>
        </Modal>
    );
};
